import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import LockIcon from '@mui/icons-material/Lock'
import SectionHeader from '../SectionHeader'
import { createRelativeFormatter } from '../studyRows'
import { RESPONDENT_CELLS, type EmotionKey } from './resultsMockData'

/** Grid of per-respondent reaction thumbnails with dominant-emotion tint. */
export default function PerRespondentGrid() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '18px', p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionHeader
        title={t('respondents.title')}
        subtitle={t('respondents.sub')}
        right={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography sx={{ fontSize: 12, color: c.ink56 }}>{t('respondents.sort')}</Typography>
            <Button startIcon={<SwapVertIcon sx={{ fontSize: 14 }} />} sx={{ color: c.ink56, fontSize: 12.5, textTransform: 'none', fontWeight: 600 }}>
              {t('respondents.sortStrongest')}
            </Button>
          </Box>
        }
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4,1fr)', sm: 'repeat(6,1fr)', md: 'repeat(12,1fr)' }, gap: 1.25 }}>
        {RESPONDENT_CELLS.map((cell, i) => (
          <RespondentCell key={i} idx={i} dominant={cell.dominant} score={cell.score} />
        ))}
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Button sx={{ color: c.ink56, fontSize: 12.5, textTransform: 'none', fontWeight: 600 }}>{t('respondents.showAll')}</Button>
      </Box>
      <Box sx={{ px: 1.75, py: 1.25, bgcolor: c.gray50, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 12, color: c.ink56 }}>
        <LockIcon sx={{ fontSize: 14 }} />
        {t('respondents.privacy')}
      </Box>
    </Box>
  )
}

function RespondentCell({ idx, dominant, score }: { idx: number; dominant: EmotionKey; score: number }) {
  const theme = useTheme()
  const { t, i18n } = useTranslation('results')
  const color = theme.affexy.emotion[dominant]
  // Identify by "when" rather than a number — staggered mock capture times.
  const capturedLabel = createRelativeFormatter(i18n.language)(
    new Date(Date.now() - (idx * 7 + 3) * 60000).toISOString(),
  )
  return (
    <Box
      sx={{
        aspectRatio: '3/4',
        borderRadius: '10px',
        overflow: 'hidden',
        position: 'relative',
        background: `linear-gradient(160deg, ${color}88, ${color}cc)`,
        cursor: 'pointer',
      }}
    >
      <svg viewBox="0 0 60 80" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }}>
        <ellipse cx="30" cy="30" rx="14" ry="18" fill="rgba(255,255,255,.85)" />
        <path d="M5 80 Q5 50 30 46 Q55 50 55 80 Z" fill="rgba(255,255,255,.7)" />
      </svg>
      <Box sx={{ position: 'absolute', left: 6, top: 6, px: 0.6, py: 0.2, borderRadius: '5px', bgcolor: 'rgba(11,11,28,.5)', backdropFilter: 'blur(4px)', fontSize: 8.5, color: 'rgba(255,255,255,.95)', fontWeight: 700, letterSpacing: '.02em' }}>
        {capturedLabel}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 6,
          right: 6,
          bottom: 6,
          px: 0.75,
          py: 0.5,
          bgcolor: 'rgba(11,11,28,.55)',
          backdropFilter: 'blur(6px)',
          borderRadius: '6px',
          fontSize: 10,
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{t(`emotion.${dominant}`)}</span>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{Math.round(score * 100)}</Box>
      </Box>
    </Box>
  )
}
